import React, { Component } from 'react';
import classnames from 'classnames';
import propTypes from 'prop-types';
import { ButtonGroup, Spinner } from 'reactstrap';

import { noop } from 'lib/noop';
import { PrimaryButton, ClearButton } from 'ui/Components';

import { AMP_PLACEMENT_SWATCHES } from '../const';
import styles from './AssetArea.css';
import AMPCard from './AMPCard';
import ExpandedAssetModal from './ExpandedAssetModal';

/**
 * asset area for amp modal
 */
export default class AssetArea extends Component {
  static propTypes = {
    asset: propTypes.object,
    cardData: propTypes.arrayOf(propTypes.object),
    isFetching: propTypes.bool,
    isPlacement: propTypes.bool,
    onAsset: propTypes.func,
    onCancel: propTypes.func,
    onInsert: propTypes.func
  };

  static defaultProps = {
    asset: null,
    cardData: [],
    isFetching: false,
    isPlacement: false,
    onAsset: noop,
    onCancel: noop,
    onInsert: noop
  };

  state = {
    collapsed: new Set(),
    expandedAsset: '',
    modal: false
  };

  /**
   * create asset url based on event current target dataset
   * and call `onAsset` prop with complete asset url
   * @param {Event} event - click event
   */
  handleAssetUpdate = event => {
    const { onAsset } = this.props;

    // most clicks happen on children of div with data so read from `currentTarget`
    const { assetSrc, height, width } = event.currentTarget.dataset;

    if (assetSrc) {
      const asset = new URL(assetSrc);

      // Do not set undefined or 0 height or width
      asset.searchParams.set('h', height || '');
      asset.searchParams.set('w', width || '');

      onAsset(asset);
    }
  };

  /**
   * handle card expand
   * @param {Event} event - click event
   */
  handleCardExpand = event => {
    event.stopPropagation();
    const { assetSrc } = event.currentTarget.dataset;

    this.setState({
      expandedAsset: assetSrc,
      modal: true
    });
  };

  /**
   * handle toggle card details
   * @param {Event} event - click event
   */
  handleCardToggle = event => {
    event.stopPropagation();
    const { assetId } = event.currentTarget.dataset;
    this.setState(ps => {
      const { collapsed } = ps;
      if (collapsed.has(assetId)) {
        collapsed.delete(assetId);
      } else {
        collapsed.add(assetId);
      }
      return { collapsed };
    });
  };

  /**
   * toggle expanded asset modal
   */
  toggle = () => {
    this.setState(ps => ({ modal: !ps.modal }));
  };

  /**
   * render amp cards based on `cardData` prop
   * @returns {Component[]} array containing amp cards
   */
  renderAmpCards = () => {
    const { asset, cardData, isFetching } = this.props;
    const { collapsed } = this.state;

    return isFetching ? (
      <div className="align-self-center" data-test-id="amp-modal-spinner">
        <Spinner color="primary" type="grow" />
        <Spinner color="primary" type="grow" />
        <Spinner color="primary" type="grow" />
      </div>
    ) : (
      cardData.map(obj => {
        // turn obj into base props for AMPCard
        const { assetId } = obj;
        let cardProps = {
          assetId,
          onExpand: this.handleCardExpand,
          onToggleDetails: this.handleCardToggle,
          tags: obj.tags,
          width: obj.imageWidth || obj.nativeDimensions?.width,
          height: obj.imageHeight || obj.nativeDimensions?.height,
          open: !collapsed.has(assetId),
          assetSrc: obj.imageUrls?.default || obj.publishedAssetUrl,
          fileType: obj.mediaType || obj.fileType,
          selected: asset && asset.pathname.includes(obj.ampAssetId || assetId)
        };

        // additional AMPCard props for Workfront ID and AMP ID filters
        cardProps = {
          ...cardProps,
          assetType: obj.editorialImageType,
          division: obj.division,
          liveDate: obj.liveDate,
          workfrontId: obj.workfrontId
        };

        return (
          <div
            data-asset-src={cardProps.assetSrc}
            data-height={cardProps.height}
            data-width={cardProps.width}
            data-test-id={`amp-modal-div-card-${assetId}`}
            key={assetId}
            onClick={this.handleAssetUpdate}
            role="presentation"
          >
            <AMPCard {...cardProps} />
          </div>
        );
      })
    );
  };

  /**
   * render a placement based on `asset` prop
   * @returns {Component} placement
   */
  renderPlacement = () => {
    const { asset } = this.props;
    if (!asset) {
      return null;
    }

    const placementStyle = {
      backgroundColor: AMP_PLACEMENT_SWATCHES[asset.pathname.split('/').pop()],
      height: `${asset.searchParams.get('h')}px`,
      width: `${asset.searchParams.get('w')}px`
    };

    return <div data-test-id="amp-modal-div-placement" style={placementStyle} />;
  };

  render() {
    const { asset, isPlacement, onCancel, onInsert } = this.props;
    const { expandedAsset, modal } = this.state;

    const assetAreaClasses = classnames(
      styles.assetArea,
      'border-left border-light',
      'd-flex flex-wrap justify-content-around',
      'overflow-auto',
      'p-4',
      'position-relative'
    );

    return (
      <React.Fragment>
        <div className={assetAreaClasses}>
          {isPlacement ? this.renderPlacement() : this.renderAmpCards()}
        </div>
        <div className={`border-secondary ${styles.insertButtonsOverlay}`}>
          <ButtonGroup className="ml-auto">
            <ClearButton
              className="mr-2"
              onClick={onCancel}
              data-test-id="amp-modal-button-cancel"
            >
              Cancel
            </ClearButton>
            <PrimaryButton
              onClick={onInsert}
              disabled={!asset}
              data-test-id="amp-modal-button-insert"
            >
              Insert
            </PrimaryButton>
          </ButtonGroup>
        </div>
        <ExpandedAssetModal asset={expandedAsset} isOpen={modal} toggle={this.toggle} />
      </React.Fragment>
    );
  }
}
